import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Calculator, TrendingUp, PiggyBank, Target, Phone, Mail, MapPin, Clock, ArrowLeft, Home, Unlock } from 'lucide-react';
import { mockCalculateRetirementPlan } from '../mock/mock';
import LeadCaptureModal from './LeadCaptureModal';

const RetirementPlanner = () => {
  const navigate = useNavigate();

  // Set page title
  React.useEffect(() => {
    document.title = 'Retirement Cash Flow Planner - AUMONLINE';
  }, []);

  const [formData, setFormData] = useState({
    currentAge: '',
    retirementAge: '60',
    lifeExpectancy: '85',
    monthlyExpenditure: '',
    swpPercentage: '4',
    expectedCAGR: '12',
    inflationRate: '6'
  });

  const [results, setResults] = useState(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showFullResults, setShowFullResults] = useState(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCalculate = () => {
    // Validate inputs
    const requiredFields = ['currentAge', 'retirementAge', 'lifeExpectancy', 'monthlyExpenditure', 'swpPercentage', 'expectedCAGR', 'inflationRate'];
    const hasEmptyFields = requiredFields.some(field => !formData[field]);
    
    if (hasEmptyFields) {
      alert('Please fill in all fields');
      return;
    }

    // Validate life expectancy > retirement age
    if (parseInt(formData.lifeExpectancy) <= parseInt(formData.retirementAge)) {
      alert('Life Expectancy must be greater than Retirement Age');
      return;
    }

    // Use mock calculation for now
    const calculatedResults = mockCalculateRetirementPlan(formData);
    setResults(calculatedResults);
    setShowPreview(true);
    setShowFullResults(false);
  };

  const handleEnd = () => {
    setResults(null);
    setShowPreview(false);
    setShowFullResults(false);
    setFormData({
      currentAge: '',
      retirementAge: '60',
      lifeExpectancy: '85',
      monthlyExpenditure: '',
      swpPercentage: '4',
      expectedCAGR: '12',
      inflationRate: '6'
    });
  };

  const handleUnlockFullRoadmap = () => {
    setIsLeadModalOpen(true);
  };

  const handleLeadSubmit = async (leadData) => {
    setIsSubmittingLead(true);
    
    try {
      const webhookPayload = {
        lead_source: 'AUMOnline Planner',
        tool_used: 'Retirement Cash Flow Planner',
        user_name: leadData.name,
        user_phone: leadData.phone,
        key_metrics: {
          target_corpus: results?.totalCorpusRequired,
          monthly_investment_needed: results?.sipRequired
        }
      };

      const response = await fetch('https://n8n.srv1138087.hstgr.cloud/webhook/planner-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });

      if (response.ok) {
        setIsLeadModalOpen(false);
        setShowFullResults(true);
      } else {
        let errorText = 'Unknown error';
        try {
          errorText = await response.text();
        } catch (e) {
          console.error('Could not read error response:', e);
        }
        throw new Error(`Failed to submit: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`There was an error submitting your details: ${errorMessage}. Please try again.`);
    } finally {
      setIsSubmittingLead(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-between items-center mb-8">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center gap-2 border-2 border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white transition-colors"
            >
              <Home className="w-5 h-5" />
              Back to Home
            </Button>
            
            <div className="flex-1 flex flex-col items-center gap-4">
              <a href="https://www.aumonline.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/5j61poys_Logo.jpg" 
                  alt="AUMONLINE Logo" 
                  className="h-20 md:h-24 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
              <a href="https://www.njwealth.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/0iyf1b7c_image.png" 
                  alt="NJ WEALTH Logo" 
                  className="h-14 md:h-16 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
            </div>
            
            <div className="w-32"></div> {/* Spacer for balance */}
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            RETIREMENT CASH FLOW PLANNER
          </h1>
        </div>

        {/* Main Content - Horizontal Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-8xl mx-auto">
          {/* Input Section */}
          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-blue-800 to-green-700 text-white">
              <CardTitle className="text-2xl md:text-3xl text-center flex items-center justify-center gap-3">
                <Calculator className="w-8 h-8" />
                Plan Your Retirement
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentAge" className="text-lg font-semibold text-gray-700">
                      Current Age
                    </Label>
                    <Input
                      id="currentAge"
                      name="currentAge"
                      type="number"
                      value={formData.currentAge}
                      onChange={handleInputChange}
                      placeholder="Enter your current age"
                      className="text-lg p-4 border-2 border-gray-300 focus:border-blue-800"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="retirementAge" className="text-lg font-semibold text-gray-700">
                      Age You Would Like to Retire
                    </Label>
                    <Input
                      id="retirementAge"
                      name="retirementAge"
                      type="number"
                      value={formData.retirementAge}
                      onChange={handleInputChange}
                      placeholder="60"
                      className="text-lg p-4 border-2 border-gray-300 focus:border-blue-800"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lifeExpectancy" className="text-lg font-semibold text-gray-700">
                      Life Expectancy
                    </Label>
                    <Input
                      id="lifeExpectancy"
                      name="lifeExpectancy"
                      type="number"
                      value={formData.lifeExpectancy}
                      onChange={handleInputChange}
                      placeholder="85"
                      className="text-lg p-4 border-2 border-gray-300 focus:border-blue-800"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="monthlyExpenditure" className="text-lg font-semibold text-gray-700">
                    Monthly Expenditure (Present Day)
                  </Label>
                  <Input
                    id="monthlyExpenditure"
                    name="monthlyExpenditure"
                    type="number"
                    value={formData.monthlyExpenditure}
                    onChange={handleInputChange}
                    placeholder="₹ Enter monthly expenses"
                    className="text-lg p-4 border-2 border-gray-300 focus:border-blue-800"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="swpPercentage" className="text-lg font-semibold text-gray-700">
                      Safe Withdrawal Rate (Recommended: 4-6%)
                    </Label>
                    <Input
                      id="swpPercentage"
                      name="swpPercentage"
                      type="number"
                      step="0.1"
                      value={formData.swpPercentage}
                      onChange={handleInputChange}
                      placeholder="4"
                      className="text-lg p-4 border-2 border-gray-300 focus:border-blue-800"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expectedCAGR" className="text-lg font-semibold text-gray-700">
                      Expected CAGR (%)
                    </Label>
                    <Input
                      id="expectedCAGR"
                      name="expectedCAGR"
                      type="number"
                      step="0.1"
                      value={formData.expectedCAGR}
                      onChange={handleInputChange}
                      placeholder="12"
                      className="text-lg p-4 border-2 border-gray-300 focus:border-blue-800"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inflationRate" className="text-lg font-semibold text-gray-700">
                    Inflation Rate (%)
                  </Label>
                  <Input
                    id="inflationRate"
                    name="inflationRate"
                    type="number"
                    step="0.1"
                    value={formData.inflationRate}
                    onChange={handleInputChange}
                    placeholder="6"
                    className="text-lg p-4 border-2 border-gray-300 focus:border-blue-800"
                  />
                </div>

                <div className="text-center space-y-4">
                  <Button
                    onClick={handleCalculate}
                    className="w-full bg-gradient-to-r from-blue-800 to-green-700 hover:from-blue-900 hover:to-green-800 text-white px-8 py-4 text-xl font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    <TrendingUp className="w-6 h-6 mr-3" />
                    RETIREMENT PLAN
                  </Button>

                  {results && (
                    <div className="flex gap-4 justify-center">
                      <Button
                        onClick={handleEnd}
                        className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-3 text-lg font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                      >
                        END
                      </Button>

                      <Dialog open={isContactOpen} onOpenChange={setIsContactOpen}>
                        <DialogTrigger asChild>
                          <Button className="bg-gradient-to-r from-green-700 to-blue-800 hover:from-green-800 hover:to-blue-900 text-white px-8 py-3 text-lg font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200">
                            Contact Us
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-center mb-4">
                              Contact AUMONLINE
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-6">
                            <div className="text-center">
                              <img 
                                src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/5j61poys_Logo.jpg" 
                                alt="AUMONLINE Logo" 
                                className="h-16 mx-auto mb-4"
                              />
                              <p className="text-lg text-gray-600">Get personalized retirement planning advice from our experts</p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                  <Phone className="w-5 h-5 text-blue-600" />
                                  <div>
                                    <p className="font-semibold text-gray-800">Phone</p>
                                    <p className="text-gray-600">+91 9845 123 456</p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                  <Mail className="w-5 h-5 text-green-600" />
                                  <div>
                                    <p className="font-semibold text-gray-800">Email</p>
                                    <p className="text-gray-600">info@aumonline.com</p>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                  <MapPin className="w-5 h-5 text-red-600" />
                                  <div>
                                    <p className="font-semibold text-gray-800">Office</p>
                                    <p className="text-gray-600">123 Financial District<br />Mysore, Karnataka 570001</p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                  <Clock className="w-5 h-5 text-yellow-600" />
                                  <div>
                                    <p className="font-semibold text-gray-800">Hours</p>
                                    <p className="text-gray-600">Mon-Fri: 9AM-6PM<br />Sat: 9AM-2PM</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg">
                              <p className="text-center text-gray-700">
                                <strong>Schedule a Free Consultation</strong><br />
                                Our certified financial advisors are ready to help you create a personalized retirement strategy.
                              </p>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          {results && showPreview && (
            <div className="space-y-6">
              <Card className="shadow-xl border-0">
                <CardHeader className="bg-gradient-to-r from-green-700 to-blue-800 text-white">
                  <CardTitle className="text-2xl md:text-3xl text-center">
                    Your Retirement Plan Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-green-100 to-green-50 p-6 rounded-xl border-l-4 border-green-700">
                      <div className="flex items-center gap-3 mb-3">
                        <PiggyBank className="w-8 h-8 text-green-700" />
                        <h3 className="text-xl font-bold text-gray-800">Total Corpus Required</h3>
                      </div>
                      <p className="text-4xl font-bold text-green-700">
                        ₹{results?.totalCorpusRequired?.toLocaleString('en-IN', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })}
                      </p>
                    </div>

                    {!showFullResults && (
                      <div className="text-center space-y-4">
                        <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                          <p className="text-lg text-gray-700 mb-4">
                            🔒 Want to see the complete breakdown including:
                          </p>
                          <ul className="text-left text-gray-600 space-y-2 mb-4 max-w-md mx-auto">
                            <li>• Inflation-adjusted monthly expenditure</li>
                            <li>• Exact SIP amount required</li>
                            <li>• Year-by-year investment projections</li>
                            <li>• Comprehensive investment summary</li>
                          </ul>
                        </div>
                        <Button
                          onClick={handleUnlockFullRoadmap}
                          className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-4 text-xl font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                        >
                          <Unlock className="w-6 h-6 mr-3" />
                          Unlock Full Roadmap
                        </Button>
                      </div>
                    )}

                    {showFullResults && (
                      <>
                        <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-6 rounded-xl border-l-4 border-blue-800">
                          <div className="flex items-center gap-3 mb-3">
                            <Target className="w-8 h-8 text-blue-800" />
                            <h3 className="text-xl font-bold text-gray-800">Monthly Expenditure at Retirement</h3>
                          </div>
                          <p className="text-3xl font-bold text-blue-800">
                            ₹{results?.inflationAdjustedExpenditure?.toLocaleString('en-IN', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            })}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {showFullResults && (
                <Card className="shadow-xl border-0">
                  <CardHeader className="bg-gradient-to-r from-orange-500 to-yellow-600 text-white">
                    <CardTitle className="text-2xl md:text-3xl text-center">
                      SIP Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8">
                    <div className="space-y-6">
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-6 rounded-xl border-l-4 border-orange-600">
                        <div className="flex items-center gap-3 mb-3">
                          <Calculator className="w-8 h-8 text-orange-600" />
                          <h3 className="text-xl font-bold text-gray-800">SIP Required Per Month</h3>
                        </div>
                        <p className="text-3xl font-bold text-yellow-600">
                          ₹{results?.sipRequired?.toLocaleString('en-IN', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          })}
                        </p>
                      </div>

                      <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-6 rounded-xl border-l-4 border-yellow-600">
                        <div className="flex items-center gap-3 mb-3">
                          <TrendingUp className="w-8 h-8 text-yellow-600" />
                          <h3 className="text-xl font-bold text-gray-800">Total Corpus Created</h3>
                        </div>
                        <p className="text-3xl font-bold text-yellow-600">
                          ₹{results?.totalCorpusCreated?.toLocaleString('en-IN', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          })}
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-lg p-4">
                        <h4 className="font-bold text-gray-800 mb-2">Investment Summary:</h4>
                        <div className="text-sm space-y-1 text-gray-700">
                          <p>• Investment Duration: {results?.yearsToRetirement} years</p>
                          <p>• Expected Returns: {formData.expectedCAGR}% per annum</p>
                          <p>• Total Investment: ₹{(results?.sipRequired * results?.yearsToRetirement * 12)?.toLocaleString('en-IN')}</p>
                          <p>• Expected Surplus: ₹{(results?.totalCorpusCreated - results?.totalCorpusRequired)?.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Lead Capture Modal */}
        <LeadCaptureModal
          isOpen={isLeadModalOpen}
          onClose={() => setIsLeadModalOpen(false)}
          onSubmit={handleLeadSubmit}
          toolName="Retirement Cash Flow Planner"
          isSubmitting={isSubmittingLead}
        />

        {/* SEBI Compliance Disclaimer */}
        <div className="mt-12 pt-8 border-t border-gray-300 max-w-8xl mx-auto">
          <div className="text-center">
            <p className="text-lg font-bold text-red-600">
              Mutual Fund investments are subject to market risks. Read all scheme-related documents carefully before investing
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetirementPlanner;